import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded'
import { Box, Button, Checkbox, FormControl, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { clickableProps } from '../../helpers/interactive'
import { maxInstallShareOf, relativePct } from '../../helpers/relativeMetric'
import { focusRingSx, linkTypographySx } from '../../helpers/styles'
import { useControlledColumns } from '../../hooks/useControlledColumns'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useUAInsightsActions, useUAInsightsScope } from '../../hooks/useUAInsightsNavigation'
import { CreativeAd } from '../../types'
import { CreativeAdCardGrid } from '../CreativeAdCardGrid/CreativeAdCardGrid'
import { CreativeAdTooltipContent } from '../CreativeAdTooltipContent/CreativeAdTooltipContent'
import { RelativeMetricBar } from '../RelativeMetricBar/RelativeMetricBar'
import { SectionCard } from '../SectionCard/SectionCard'

type ViewMode = 'table' | 'cards'
const VIEW_MODES = ['table', 'cards'] as const
const isViewMode = (input: unknown): input is ViewMode => typeof input === 'string' && (VIEW_MODES as readonly string[]).includes(input)
const VIEW_MODE_STORAGE_KEY = 'ua-insights:creative-ads:view-mode'

type CardSortKey = 'performance' | 'gameName' | 'duration'
const CARD_SORT_KEYS = ['performance', 'gameName', 'duration'] as const
const isCardSortKey = (input: unknown): input is CardSortKey => typeof input === 'string' && (CARD_SORT_KEYS as readonly string[]).includes(input)
const CARD_SORT_STORAGE_KEY = 'ua-insights:creative-ads:card-sort'

interface CreativeAdsTableProps {
  rows: CreativeAd[]
  /** When provided, renders a compare column + action button. */
  onCompare?: (adId1: string, adId2: string) => void
}

/**
 * Per-render inputs the cells need. Passed through `GRTable`'s `customProps`
 * rather than captured in the column closures so ticking a compare checkbox
 * doesn't rebuild every column definition.
 */
interface AdRowCustomProps {
  selectedAdIds: ReadonlySet<string>
  toggleAdSelection: (adId: string) => void
  /** Strongest install share in the current view; the Relative Impressions baseline. */
  maxInstallShare: number
  currentMonth: string | null
}

export const CreativeAdsTable: FC<CreativeAdsTableProps> = ({ rows, onCompare }) => {
  const { t } = useTranslation('ua-insights')
  const { openAd, openGame } = useUAInsightsActions()
  const { month: currentMonth } = useUAInsightsScope()
  const [selectedAdIds, setSelectedAdIds] = useState<Set<string>>(new Set())
  // View + sort preferences are stored per browser (localStorage) so they
  // persist across reloads and between game pages — they're personal display
  // preferences, not shared analytical state, so we deliberately keep them
  // out of the URL.
  const [viewMode, setViewMode] = usePersistedState<ViewMode>(VIEW_MODE_STORAGE_KEY, 'cards', isViewMode)
  const [cardSort, setCardSort] = usePersistedState<CardSortKey>(CARD_SORT_STORAGE_KEY, 'performance', isCardSortKey)

  const toggleAdSelection = useCallback((adId: string) => {
    setSelectedAdIds((prev) => {
      const next = new Set(prev)
      if (next.has(adId)) {
        next.delete(adId)
      } else if (next.size < 2) {
        next.add(adId)
      }
      return next
    })
  }, [])

  const handleCompare = useCallback(() => {
    if (selectedAdIds.size !== 2 || !onCompare) return
    const [id1, id2] = Array.from(selectedAdIds)
    onCompare(id1, id2)
  }, [selectedAdIds, onCompare])

  const handleViewModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, next: ViewMode | null) => {
      // ToggleButtonGroup emits `null` when the active button is re-clicked.
      // We treat that as a no-op (we always want exactly one mode selected).
      if (next) setViewMode(next)
    },
    [setViewMode]
  )

  // The Relative Impressions bar shows each row's install share relative to
  // the strongest performer in the current view, so the leader always fills
  // the bar and weaker creatives are visually proportional.
  const maxInstallShare = useMemo(() => maxInstallShareOf(rows), [rows])

  // Pre-sort rows for the cards view. The table view ignores this and uses
  // GRTable's column-header sorting instead — that keeps the table experience
  // unchanged and avoids two competing sort sources.
  const sortedCardRows = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' })
    const next = [...rows]
    switch (cardSort) {
      case 'gameName':
        next.sort((a, b) => collator.compare(a.gameName, b.gameName))
        break
      case 'duration':
        next.sort((a, b) => a.videoDurationSeconds - b.videoDurationSeconds)
        break
      case 'performance':
      default:
        next.sort((a, b) => (b.installShare ?? 0) - (a.installShare ?? 0))
        break
    }
    return next
  }, [rows, cardSort])

  const customProps = useMemo<AdRowCustomProps>(
    () => ({ selectedAdIds, toggleAdSelection, maxInstallShare, currentMonth }),
    [selectedAdIds, toggleAdSelection, maxInstallShare, currentMonth]
  )

  const defaultColumns: GRTableColumn<CreativeAd, AdRowCustomProps>[] = useMemo(
    () => [
      ...(onCompare
        ? [
            {
              labelAccessor: '',
              accessor: ({ row, customTableProps }: { row: CreativeAd; customTableProps?: AdRowCustomProps }) => (
                <Checkbox
                  size="small"
                  checked={!!customTableProps?.selectedAdIds.has(row.id)}
                  disabled={!customTableProps?.selectedAdIds.has(row.id) && (customTableProps?.selectedAdIds.size ?? 0) >= 2}
                  onChange={() => customTableProps?.toggleAdSelection(row.id)}
                  sx={{ p: 0.25 }}
                />
              ),
              width: 40,
              cellProps: { align: 'center' as const, sx: { width: 40, maxWidth: 40 } },
              headerCellProps: { align: 'center' as const, sx: { width: 40, maxWidth: 40 } },
            },
          ]
        : []),
      {
        labelAccessor: t('col_creative_ad'),
        accessor: ({ row, customTableProps }) => (
          <GRTooltip
            hidden={row.dominantMotivations.length === 0 && row.tags.length === 0}
            content={
              <CreativeAdTooltipContent
                ad={row}
                currentMonth={customTableProps?.currentMonth ?? null}
                maxInstallShare={customTableProps?.maxInstallShare ?? 0}
              />
            }
            tooltipProps={{ enterDelay: 500, enterNextDelay: 200, placement: 'right' }}
            styles={{ display: 'inline-block' }}
          >
            <Box
              aria-label={t('open_creative_ad', { game: row.gameName })}
              {...clickableProps(() => openAd(row.id))}
              sx={{ cursor: 'pointer', py: 0.5, ...focusRingSx() }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: 118,
                  height: 118,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  '&:hover .ad-play-overlay': { opacity: 0.8 },
                }}
              >
                {row.thumbUrl && (
                  <Box
                    component="img"
                    src={row.thumbUrl}
                    alt={row.gameName}
                    loading="lazy"
                    decoding="async"
                    sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  />
                )}
                <Box
                  className="ad-play-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 200ms ease-in-out',
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PlayArrowRoundedIcon sx={{ color: 'common.white', fontSize: 32 }} />
                  </Box>
                </Box>
              </Box>
            </Box>
          </GRTooltip>
        ),
        sortable: false,
        width: 134,
        cellProps: { align: 'center', sx: { width: 134, maxWidth: 134 } },
        headerCellProps: { align: 'center', sx: { fontWeight: 'bold', width: 134, maxWidth: 134 } },
      },
      {
        labelAccessor: t('col_game_name'),
        accessor: ({ row }) => (
          <Typography variant="body2" sx={linkTypographySx} {...clickableProps(() => openGame(row.gameId))}>
            {row.gameName}
          </Typography>
        ),
        sortAccessor: 'gameName',
        sortable: true,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
      },
      {
        labelAccessor: t('col_duration'),
        accessor: ({ row }) => t('seconds', { count: row.videoDurationSeconds }),
        sortAccessor: 'videoDurationSeconds',
        sortable: true,
        width: 100,
        cellProps: { align: 'center' },
        headerCellProps: { align: 'center', sx: { fontWeight: 'bold' } },
      },
      {
        labelAccessor: t('col_network'),
        accessor: ({ row }) => row.network.name,
        sortAccessor: ({ row }) => row.network.name,
        sortable: true,
        cellProps: { align: 'center' },
        headerCellProps: { align: 'center', sx: { fontWeight: 'bold' } },
      },
      {
        labelAccessor: t('col_ad_performance'),
        accessor: ({ row, customTableProps }) => {
          // "Relative Impressions" is relative: the strongest performer in
          // the current view is 100% and weaker creatives scale proportionally.
          // The label and bar therefore share the same percentage so the cell
          // reads as a single coherent value.
          return <RelativeMetricBar pct={relativePct(row.installShare ?? 0, customTableProps?.maxInstallShare ?? 0)} sx={{ minWidth: 140 }} />
        },
        sortAccessor: ({ row }) => row.installShare ?? 0,
        sortable: true,
        sortOrder: SortOrder.DESC,
        width: 300,
        cellProps: { align: 'center', sx: { width: 300, minWidth: 300, maxWidth: 300 } },
        headerCellProps: { align: 'center', sx: { fontWeight: 'bold', width: 300, minWidth: 300, maxWidth: 300 } },
      },
    ],
    [t, openAd, openGame, onCompare]
  )

  const [columns, setColumns] = useControlledColumns(defaultColumns)

  return (
    <SectionCard
      title={
        <Box sx={{ p: '5px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
            {t('hot_not_ads')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {t('ad_count', { count: rows.length })}
          </Typography>
        </Box>
      }
      headerAction={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {viewMode === 'cards' && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="creative-ads-card-sort-label">{t('sort_by')}</InputLabel>
              <Select
                labelId="creative-ads-card-sort-label"
                label={t('sort_by')}
                value={cardSort}
                onChange={(event) => {
                  const next = event.target.value
                  if (isCardSortKey(next)) setCardSort(next)
                }}
                sx={{ height: 40, '& .MuiSelect-select': { py: 1 } }}
              >
                <MenuItem value="performance">{t('sort_performance')}</MenuItem>
                <MenuItem value="gameName">{t('sort_game_name')}</MenuItem>
                <MenuItem value="duration">{t('sort_duration')}</MenuItem>
              </Select>
            </FormControl>
          )}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={handleViewModeChange}
            aria-label={t('view_mode_label')}
            sx={{
              // Pill-shaped segmented control matching the Select's rounded
              // outline so the header controls share a consistent visual
              // language.
              height: 40,
              '& .MuiToggleButtonGroup-firstButton': {
                borderTopLeftRadius: '20px',
                borderBottomLeftRadius: '20px',
                pl: 1.75,
              },
              '& .MuiToggleButtonGroup-lastButton': {
                borderTopRightRadius: '20px',
                borderBottomRightRadius: '20px',
                pr: 1.75,
              },
              '& .MuiToggleButton-root': {
                px: 1.25,
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                  '&:hover': { backgroundColor: (theme) => theme.palette.action.selected },
                },
              },
            }}
          >
            <ToggleButton value="cards" aria-label={t('view_mode_cards')}>
              <GridViewRoundedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="table" aria-label={t('view_mode_table')}>
              <ViewListRoundedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          {onCompare && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<CompareArrowsIcon />}
              disabled={selectedAdIds.size !== 2}
              onClick={handleCompare}
              sx={{ height: 40 }}
            >
              {t('compare_selected')}
              {selectedAdIds.size > 0 && ` (${selectedAdIds.size}/2)`}
            </Button>
          )}
        </Box>
      }
      disableBodyPadding
    >
      {viewMode === 'table' ? (
        <GRTable
          rows={rows}
          columns={columns}
          onColumnsUpdated={setColumns}
          customProps={customProps}
          striped
          hoverable
          rowIdKey="id"
          noRowsLabel={t('no_ads')}
        />
      ) : (
        <CreativeAdCardGrid
          rows={sortedCardRows}
          maxInstallShare={maxInstallShare}
          selectedAdIds={onCompare ? selectedAdIds : undefined}
          onToggleSelection={onCompare ? toggleAdSelection : undefined}
        />
      )}
    </SectionCard>
  )
}
